import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp, Calculator, Users, CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface FAQItem {
  question: string;
  answer: string;
  category: "bidding" | "operations" | "quality" | "platform";
}

const faqs: FAQItem[] = [
  // 1. Commercial Bidding & Estimating
  {
    question: "How do you calculate a commercial cleaning bid using square footage?",
    answer:
      "To calculate a commercial cleaning bid: (1) Divide cleanable square footage by the ISSA standard production rate (e.g. 3,000 sq ft/hr for commercial offices) to determine labor hours per clean. (2) Multiply hours by your burdened labor wage (base wage + 11-15% payroll taxes/workers' comp). (3) Add 2-4% for chemical supplies and 10% operating overhead. (4) Divide direct costs by (1 - Target Gross Margin, typically 35-50%) to establish your monthly contract price.",
    category: "bidding",
  },
  {
    question: "What is the average commercial cleaning rate per square foot?",
    answer:
      "Commercial cleaning rates range from $0.08 to $0.22 per square foot per month for recurring 3x–5x weekly office cleaning. Medical and surgical clinics range from $0.18 to $0.35 per sq ft due to strict terminal sanitization protocols. One-time post-construction or commercial deep cleans range from $0.25 to $0.50 per square foot.",
    category: "bidding",
  },
  {
    question: "What is the ISSA 612 standard and how is it used in estimating?",
    answer:
      "The ISSA 612 standard is the industry benchmark published by the International Sanitary Supply Association detailing cleaning times. Standard commercial office space averages 2,500 to 3,500 sq ft/hr, medical facilities average 1,500 to 2,200 sq ft/hr, and industrial warehouses with auto-scrubbers average 5,000 to 8,000 sq ft/hr.",
    category: "bidding",
  },
  {
    question: "What gross profit margin should a commercial cleaning company target?",
    answer:
      "A healthy commercial cleaning contractor targets a Gross Profit Margin of 40% to 50% and a Net Profit Margin (EBITDA) of 20% to 30%. Operating below a 30% gross margin leaves zero buffer for cleaner turnover, supervisor re-cleans, or insurance rate hikes.",
    category: "bidding",
  },
  {
    question: "How do you calculate burdened labor costs for janitorial employees?",
    answer:
      "Loaded labor burden adds 11% to 16% on top of base hourly wages for W-2 cleaners. It comprises FICA Social Security (6.2%), Medicare (1.45%), Federal/State Unemployment FUTA/SUTA (1.5%–4.0%), and Janitorial Workers' Compensation Insurance Code 9014 (2.5%–5.5%). An $18/hr base wage equals an actual loaded cost of $20.46 to $21.00/hr.",
    category: "bidding",
  },

  // 2. Operations, Scheduling & Crew
  {
    question: "How do you optimize multi-building cleaning routes to eliminate windshield time?",
    answer:
      "Cluster commercial accounts geographically into tight 5-mile route corridors, enforce mandatory 30-minute transition buffers between multi-site shifts, and pair dedicated building leads with mobile floater technicians who handle emergency coverage.",
    category: "operations",
  },
  {
    question: "What is the difference between 1099 cleaning subcontractors and W-2 employees?",
    answer:
      "1099 subcontractors must own an independent legal business entity (LLC/Corp), provide their own equipment and chemicals, carry their own General Liability and Workers' Comp insurance naming your company as Additional Insured, and maintain complete behavioral independence. W-2 employees are directed on exact hours, methods, and provided company equipment.",
    category: "operations",
  },
  {
    question: "What Certificate of Insurance (COI) limits are required for commercial janitorial accounts?",
    answer:
      "Commercial property managers standardly require: Minimum $1,000,000 per Occurrence / $2,000,000 Aggregate General Liability, $500,000 Workers' Compensation, and your company (or your subcontractor) listed as 'Additional Insured' with primary and non-contributory wording.",
    category: "operations",
  },
  {
    question: "How does mobile check-in verification work for field cleaners?",
    answer:
      "Field staff use the mobile app to verify on-site arrival and departure with location validation, review facility-specific access codes and alarm steps, and complete digital room inspection checklists before clocking out.",
    category: "operations",
  },
  {
    question: "How do commercial cleaners control chemical and supply costs?",
    answer:
      "To keep supply expenses between 2% and 4% of revenue: Ban ready-to-use (RTU) spray bottles and switch to wall-mounted chemical dilution dispensers, maintain a strict 2-week PAR inventory in janitor closets, and submit weekly order forms.",
    category: "operations",
  },

  // 3. Quality Control, Retention & SLAs
  {
    question: "What is the standard SLA response time for commercial cleaning client complaints?",
    answer:
      "Industry best practice enforces a 2-Hour Urgent SLA: Acknowledge every complaint within 15 minutes, perform a site re-clean within 2 hours, and deliver a written Corrective Action Plan (CAP) detailing root cause and preventive inspection steps within 24 hours.",
    category: "quality",
  },
  {
    question: "How does a 100-point commercial cleaning inspection rubric work?",
    answer:
      "A 100-point rubric weights high-risk zones: Restroom Disinfection (35 pts), Floor & Carpet Care (25 pts), Lobby & Glass (20 pts), and Dusting & Trash (20 pts). Any score below 85% triggers an automatic re-clean work order and supervisor re-audit within 48 hours.",
    category: "quality",
  },
  {
    question: "What are the 5 APPA custodial quality levels?",
    answer:
      "The APPA standards define: Level 1 (Orderly Spotlessness - Executive & Medical), Level 2 (Ordinary Tidiness - Class A Office standard), Level 3 (Casual Inattention - Budget & Municipal), Level 4 (Moderate Dinginess), and Level 5 (Unkempt Neglect). Most commercial contracts specify APPA Level 2.",
    category: "quality",
  },
  {
    question: "How do you run a Quarterly Business Review (QBR) with property managers?",
    answer:
      "A 20-minute executive QBR follows a 4-part agenda: (1) Past Quarter SLA & QA inspection audit review, (2) Resolution confirmation of any past tickets, (3) Upcoming seasonal project recommendations (e.g. carpet extraction, VCT strip/wax), and (4) Contract extension confirmation.",
    category: "quality",
  },
  {
    question: "Why do commercial cleaning clients cancel contracts?",
    answer:
      "Over 70% of cancellations stem from 'silent degradation'—gradual decline in cleaning detail combined with zero communication from company leadership. Proactive monthly inspection reports and bi-weekly email check-ins reduce annual contract churn below 3%.",
    category: "quality",
  },

  // 4. ManageClean OS Platform & Features
  {
    question: "How does ManageClean OS generate commercial cleaning proposals?",
    answer:
      "ManageClean OS embeds ISSA 612 production rate standards, square-footage calculators, and burdened labor multipliers to generate polished, client-ready PDF proposals with scope matrices, SLA terms, and integrated e-signatures in under 60 seconds.",
    category: "platform",
  },
  {
    question: "Can property managers sign cleaning contracts electronically on mobile?",
    answer:
      "Yes. Clients receive a secure, tokenized digital proposal link where they can review the itemized scope of work, select optional floor care add-ons, and execute legally binding electronic signatures from any phone, tablet, or desktop.",
    category: "platform",
  },
  {
    question: "Can I manage multiple commercial facilities under one property management client?",
    answer:
      "Yes. ManageClean OS supports parent-child account hierarchies where a property management client can have dozens of commercial buildings, each with distinct cleanable square footage, alarm access codes, and shift schedules.",
    category: "platform",
  },
  {
    question: "What plans are available, and is there a free plan for solo contractors?",
    answer:
      "ManageClean OS offers a Basic Free Forever plan (1 user, unlimited bids, unlimited clients & leads, equipment tracking), a Starter tier ($29/mo), Professional tier ($79/mo), and Team plans ($99-$349/mo) with pooled AI credits and unlimited team members.",
    category: "platform",
  },
  {
    question: "How does data security and encryption work in ManageClean OS?",
    answer:
      "ManageClean OS uses bank-grade 256-bit encryption for all data in transit and at rest, multi-tenant role-based access control, encrypted storage for facility alarm codes, and SOC 2 Type II compliant cloud hosting infrastructure.",
    category: "platform",
  },
];

const categoryLabels = {
  bidding: "Bidding & Estimating",
  operations: "Operations & Scheduling",
  quality: "Quality Control & SLAs",
  platform: "Platform & Capabilities",
};

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const filteredFaqs =
    activeCategory === "all" ? faqs : faqs.filter((faq) => faq.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white selection:bg-blue-100 selection:text-blue-900">
      <SEO
        title="Commercial Cleaning FAQs | Operations, Estimating & ManageClean OS"
        description="Frequently asked questions about commercial cleaning estimating, ISSA 612 production rates, crew dispatching, SLA agreements, and ManageClean OS platform features."
        keywords={[
          "commercial cleaning FAQ",
          "janitorial estimating questions",
          "how to calculate cleaning square footage",
          "ISSA production rates FAQ",
          "commercial cleaning software FAQ",
        ]}
        url="/success-hub/faq"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Success Hub", url: "/success-hub" },
            { name: "FAQ", url: "/success-hub/faq" },
          ]),
          schemas.createFAQSchema(
            faqs.map((f) => ({ question: f.question, answer: f.answer }))
          ),
        ]}
      />
      <LandingNavbar />

      {/* Header */}
      <section className="pb-12 pt-28 text-center">
        <div className="container mx-auto max-w-4xl px-4">
          <Link
            to="/success-hub"
            className="mb-6 inline-flex items-center text-sm text-slate-600 transition-colors hover:text-blue-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Success Hub
          </Link>

          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-pink-100 text-pink-600 shadow-sm">
            <HelpCircle className="h-8 w-8" />
          </div>

          <h1 className="mt-6 text-3xl font-extrabold text-slate-900 md:text-5xl">
            Frequently Asked Questions
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
            Answers to top questions regarding commercial cleaning estimating formulas, crew dispatching, quality control, and ManageClean OS capabilities.
          </p>

          {/* Category Tabs */}
          <div className="mt-10 flex flex-wrap justify-center gap-2">
            <button
              onClick={() => {
                setActiveCategory("all");
                setOpenIndex(null);
              }}
              className={`rounded-full px-5 py-2 text-sm font-bold transition-all ${
                activeCategory === "all"
                  ? "bg-slate-900 text-white shadow-sm"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              All Questions ({faqs.length})
            </button>
            {Object.entries(categoryLabels).map(([key, label]) => (
              <button
                key={key}
                onClick={() => {
                  setActiveCategory(key);
                  setOpenIndex(null);
                }}
                className={`rounded-full px-5 py-2 text-sm font-bold transition-all ${
                  activeCategory === key
                    ? "bg-blue-600 text-white shadow-sm"
                    : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion List */}
      <section className="pb-24">
        <div className="container mx-auto max-w-3xl px-4">
          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.03 }}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:border-slate-300"
                >
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="flex w-full items-center justify-between p-6 text-left font-bold text-slate-900 hover:bg-slate-50"
                  >
                    <span className="text-base md:text-lg">{faq.question}</span>
                    <div
                      className={`ml-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-transform ${
                        isOpen ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </div>
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="border-t border-slate-100 bg-slate-50/50 p-6 text-slate-700 leading-relaxed">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Interactive Calculator Banner */}
          <div className="mt-16 rounded-3xl bg-gradient-to-br from-slate-900 to-blue-950 p-8 text-center text-white shadow-xl">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/20 text-blue-400">
              <Calculator className="h-6 w-6" />
            </div>
            <h3 className="mt-4 text-2xl font-bold md:text-3xl">Want to Calculate Your Next Bid in Seconds?</h3>
            <p className="mx-auto mt-2 max-w-xl text-slate-300">
              Try our free interactive Commercial Cleaning Bid Calculator powered by official ISSA 612 production rate standards.
            </p>
            <div className="mt-6 flex justify-center gap-4">
              <Link to="/tools/commercial-cleaning-bid-calculator">
                <Button size="lg" className="bg-blue-600 font-bold text-white hover:bg-blue-700">
                  Try Free Bid Calculator <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
